/**
 * Семенная позиция операции сева — сорт, норма высева, густота посева.
 *
 * Требование founder (UAT 20.08, прод v1.1746, диалог «Материалы операции
 * „Сев"»): «При добавлении операции сев, у нас всегда добавляются: 1. Сорт-
 * гибрид 2. Норма на га — кг/га, п.е./га 3. Густота посева — шт/га, тыс. шт/га,
 * млн шт/га».
 *
 * Модуль ЧИСТЫЙ (без React и без сети): правила «когда поля показывать», разбор
 * чисел и производная норма живут здесь, а диалог только рисует. Так их можно
 * гонять юнит-тестами на цифрах, а не кликами.
 */

/** Единицы, в которых считают ГУСТОТУ посева. Порядок — от мелкой к крупной. */
export const DENSITY_UNIT_CODES = ['pcs', 'thousand_pcs', 'mln_pcs'] as const;

/**
 * Единицы, в которых агроном задаёт НОРМУ ВЫСЕВА (числитель; знаменатель —
 * гектар). `sowing_unit` («п.е.») приезжает в каталог отдельной миграцией —
 * пока её нет, селектор честно покажет только килограммы, а не выдуманную
 * строку.
 */
export const SEED_RATE_UNIT_CODES = ['kg', 'sowing_unit'] as const;

/** Сколько штук в одной единице густоты. */
const DENSITY_UNIT_PCS: Record<string, number> = {
  pcs: 1,
  thousand_pcs: 1_000,
  mln_pcs: 1_000_000,
};

export interface UnitLike {
  id: string;
  code: string;
  symbol: string;
}

export interface UnitOption {
  id: string;
  code: string;
  /** То, что видит агроном: «кг/га», «тыс. шт/га». */
  label: string;
}

/**
 * Показывать ли строке материала поля сева.
 *
 * Оба условия обязательны: операция — сев (машинный тег блока), позиция — из
 * раздела «Семена». Удобрению, которое вносят вместе с севом, сорт с густотой
 * не нужны — у него только норма, как и было.
 */
export function isSeedMaterial(blockKind: string | null | undefined, section: string | null | undefined): boolean {
  return blockKind === 'sowing' && section === 'seed';
}

/**
 * Единицы для селектора в порядке `codes`. Текущая единица строки добавляется,
 * даже если её нет в списке: молча подменить единицу, которая уже стоит в
 * карте, — это тихая порча данных.
 */
export function unitOptions(
  units: readonly UnitLike[] | undefined,
  codes: readonly string[],
  currentId?: string | null,
): UnitOption[] {
  const byCode = new Map(units?.map((u) => [u.code, u]) ?? []);
  const picked: UnitOption[] = [];
  codes.forEach((code) => {
    const unit = byCode.get(code);
    if (unit) picked.push({ id: unit.id, code: unit.code, label: `${unit.symbol}/га` });
  });
  if (currentId && !picked.some((o) => o.id === currentId)) {
    const current = units?.find((u) => u.id === currentId);
    if (current) picked.push({ id: current.id, code: current.code, label: `${current.symbol}/га` });
  }
  return picked;
}

/**
 * Разбор числа из поля ввода.
 *
 * «Пусто ≠ мусор»: пустая строка — это `null` (поле не заполнено, законно), а
 * «абв» или «-5» — это `'invalid'`, о котором форма обязана сказать словами, а
 * не тихо оставить прежнее значение.
 */
export function parseOptionalNumber(raw: string): number | null | 'invalid' {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return 'invalid';
  return value;
}

/**
 * Норма высева, которая следует из густоты и массы 1000 семян: кг/га.
 *
 * Именно кг/га, а НЕ п.е./га: из густоты и МТС выводится масса, а «сколько
 * семян в посевной единице» — отдельный норматив, которого в системе нет.
 * Выдумывать его («обычно 150 000») нельзя — у разных культур и фасовок он
 * разный, и агроном получил бы уверенно неправильное число.
 *
 * МТС в справочнике культур — диапазон (min/max), поэтому и ответ диапазон.
 * Значение ПОДСКАЗКА: ничего не подставляем в поле нормы, агроном вводит сам.
 */
export function seedRateFromDensity(
  density: number | null,
  densityUnitCode: string | null | undefined,
  tkwMinG: number | null | undefined,
  tkwMaxG: number | null | undefined,
): { minKgHa: number; maxKgHa: number } | null {
  const perUnit = densityUnitCode ? DENSITY_UNIT_PCS[densityUnitCode] : undefined;
  if (density === null || density <= 0 || !perUnit) return null;
  const min = tkwMinG ?? tkwMaxG;
  const max = tkwMaxG ?? tkwMinG;
  if (!min || !max || min <= 0 || max <= 0) return null;
  const seeds = density * perUnit;
  // кг/га = штук/га × г за 1000 семян / 1000 (штук) / 1000 (г в кг)
  return {
    minKgHa: (seeds * min) / 1_000_000,
    maxKgHa: (seeds * max) / 1_000_000,
  };
}
